import React, { PropsWithChildren, useCallback } from 'react'
import { useDebounceFn } from 'ahooks'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useIndeedFlexIssuesFiltersQueryState } from '../shared/hooks/useIndeedFlexIssuesFiltersQueryState'
import { IndeedFlexShiftIssue } from '../shared/constants/shiftIssues'
import { useIndeedFlexClientsQuery } from 'queries/admin/indeedFlex/useIndeedFlexClientsQuery'
import { useLinkIndeedFlexLocationMutation } from 'queries/admin/indeedFlex/useLinkIndeedFlexLocationMutation'
import { useLinkIndeedFlexPositionMutation } from 'queries/admin/indeedFlex/useLinkIndeedFlexPositionMutation'

type IndeedFlexContextState = {
  state: {
    clients: NonNullable<
      ReturnType<typeof useIndeedFlexClientsQuery>['data']
    >['items']
    filters: {
      ifClientIds: number[] | null
      companyIds: number[] | null
      issues: IndeedFlexShiftIssue[] | null
      daysAgo: number
    }
    linkIndeedFlexPositionMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    linkIndeedFlexLocationMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    setFilters: ReturnType<typeof useIndeedFlexIssuesFiltersQueryState>[1]
    linkIndeedFlexPosition: ReturnType<
      typeof useLinkIndeedFlexPositionMutation
    >['mutate']
    resetLinkIndeedFlexPosition: ReturnType<
      typeof useLinkIndeedFlexPositionMutation
    >['reset']
    linkIndeedFlexLocation: ReturnType<
      typeof useLinkIndeedFlexLocationMutation
    >['mutate']
    resetLinkIndeedFlexLocation: ReturnType<
      typeof useLinkIndeedFlexLocationMutation
    >['reset']
  }
}

const [Context, useIndeedFlexContextValue] =
  createContext<IndeedFlexContextState>({
    name: 'IndeedFlexContext',
  })

export const IndeedFlexProvider = ({ children }: PropsWithChildren) => {
  const { data: clients } = useIndeedFlexClientsQuery()
  const [filters, setFiltersImmediate] = useIndeedFlexIssuesFiltersQueryState()
  const { run: debouncedSetFilters } = useDebounceFn(setFiltersImmediate, {
    wait: 300,
  })

  const setFilters = useCallback<
    ReturnType<typeof useIndeedFlexIssuesFiltersQueryState>[1]
  >(
    (values, options) => {
      debouncedSetFilters(values, options)
      return Promise.resolve(new URLSearchParams())
    },
    [debouncedSetFilters]
  )

  const {
    mutate: linkIndeedFlexPosition,
    isPending: isLinkIndeedFlexPositionPending,
    isError: isLinkIndeedFlexPositionError,
    isSuccess: isLinkIndeedFlexPositionSuccess,
    error: linkIndeedFlexPositionError,
    reset: resetLinkIndeedFlexPosition,
  } = useLinkIndeedFlexPositionMutation()

  const {
    mutate: linkIndeedFlexLocation,
    isPending: isLinkIndeedFlexLocationPending,
    isError: isLinkIndeedFlexLocationError,
    isSuccess: isLinkIndeedFlexLocationSuccess,
    error: linkIndeedFlexLocationError,
    reset: resetLinkIndeedFlexLocation,
  } = useLinkIndeedFlexLocationMutation()

  const value: IndeedFlexContextState = {
    state: {
      clients: clients?.items ?? [],
      filters,
      linkIndeedFlexPositionMutationState: {
        isPending: isLinkIndeedFlexPositionPending,
        isError: isLinkIndeedFlexPositionError,
        isSuccess: isLinkIndeedFlexPositionSuccess,
        errorMessage: parseErrorMessage(linkIndeedFlexPositionError),
      },
      linkIndeedFlexLocationMutationState: {
        isPending: isLinkIndeedFlexLocationPending,
        isError: isLinkIndeedFlexLocationError,
        isSuccess: isLinkIndeedFlexLocationSuccess,
        errorMessage: parseErrorMessage(linkIndeedFlexLocationError),
      },
    },
    actions: {
      setFilters,
      linkIndeedFlexPosition,
      resetLinkIndeedFlexPosition,
      linkIndeedFlexLocation,
      resetLinkIndeedFlexLocation,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useIndeedFlexContextValue }
